import { useCallback, useEffect, useState } from "react";
import { uploadUrl } from "../../lib/assets";
import {
  buyMailtoHref,
  keywordLine,
  lightboxDescription,
} from "../../lib/photographyUtils";

function BuyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

/**
 * @param {{ lightbox: { list: import("../../data/photography").Photo[], index: number } | null, onClose: () => void, onChangeIndex: (index: number) => void, shareUrl?: string | null }} props
 */
export default function PhotoLightbox({ lightbox, onClose, onChangeIndex, shareUrl = null }) {
  const [expanded, setExpanded] = useState(false);
  const [shareLabel, setShareLabel] = useState("COPY LINK");

  const navigate = useCallback(
    (delta) => {
      if (!lightbox) return;
      const next =
        (lightbox.index + delta + lightbox.list.length) % lightbox.list.length;
      onChangeIndex(next);
      setExpanded(false);
    },
    [lightbox, onChangeIndex]
  );

  useEffect(() => {
    if (!lightbox) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowLeft") navigate(-1);
      else if (event.key === "ArrowRight") navigate(1);
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightbox, navigate, onClose]);

  useEffect(() => {
    setExpanded(false);
    setShareLabel("COPY LINK");
  }, [lightbox?.index, shareUrl]);

  const copyShareLink = useCallback(async () => {
    if (!shareUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareLabel("LINK COPIED");
      window.setTimeout(() => setShareLabel("COPY LINK"), 2000);
    } catch {
      setShareLabel("COPY FAILED");
      window.setTimeout(() => setShareLabel("COPY LINK"), 2000);
    }
  }, [shareUrl]);

  if (!lightbox) return null;

  const photo = lightbox.list[lightbox.index];
  const indexLabel = `${String(lightbox.index + 1).padStart(2, "0")} / ${String(lightbox.list.length).padStart(2, "0")}`;

  return (
    <div
      className="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={photo.t}
      onClick={() => {
        if (expanded) setExpanded(false);
      }}
    >
      <div className="photo-lightbox__top">
        <span className="photo-lightbox__index">{indexLabel}</span>
        <button type="button" className="photo-lightbox__close" onClick={onClose}>
          CLOSE <span aria-hidden="true">×</span>
        </button>
      </div>

      <div className="photo-lightbox__stage">
        <div
          className="photo-lightbox__image"
          style={
            photo.src
              ? { backgroundImage: `url('${uploadUrl(photo.src)}')` }
              : { background: photo.grad, backgroundImage: "none" }
          }
        />
      </div>

      <button
        type="button"
        className="photo-lightbox__arrow photo-lightbox__arrow--prev"
        aria-label="Previous photograph"
        onClick={(event) => {
          event.stopPropagation();
          navigate(-1);
        }}
      >
        ‹
      </button>
      <button
        type="button"
        className="photo-lightbox__arrow photo-lightbox__arrow--next"
        aria-label="Next photograph"
        onClick={(event) => {
          event.stopPropagation();
          navigate(1);
        }}
      >
        ›
      </button>

      <div className="photo-lightbox__bottom">
        <div className="photo-lightbox__caption">
          <button
            type="button"
            className={`photo-lightbox__title${expanded ? " is-expanded" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((open) => !open);
            }}
          >
            <span>{photo.t}</span>
            <span className="photo-lightbox__chev" aria-hidden="true">
              +
            </span>
          </button>
          <div className={`photo-lightbox__details${expanded ? " is-open" : ""}`}>
            <div className="photo-lightbox__keywords">{keywordLine(photo)}</div>
            <p className="photo-lightbox__desc">{lightboxDescription(photo)}</p>
          </div>
        </div>

        <div className="photo-lightbox__actions">
          {shareUrl ? (
            <button
              type="button"
              className="photo-lightbox__share"
              onClick={(event) => {
                event.stopPropagation();
                void copyShareLink();
              }}
            >
              {shareLabel}
            </button>
          ) : null}
          <a href={buyMailtoHref(photo)} className="photo-lightbox__buy" onClick={(e) => e.stopPropagation()}>
            <BuyIcon />
            BUY
          </a>
        </div>
      </div>
    </div>
  );
}
